export type PseudoClass = "hover" | "focus" | "disabled"

/**
 *  This is a helper function that allows for determining the correct Tailwind CSS
 *  class names for a given class names string and a list of pseudo classes. All
 *  classes provided with the input string will be prefixed with supplied pseudo classes.
 *
 *  Note that somes classes might not work correctly. This is due to Tailwind's
 *  class generation system. In essence, the system is a magical regex over our
 *  source code and it can't detect dynamic class names. To overcome this, we
 *  force a certain list of safe classes that will be generated via the safelist
 *  functionality. It's better to stick with these classes when using this function.
 *
 *  @see https://tailwindcss.com/docs/content-configuration#safelisting-classes
 */
function determinePseudoClass(input: string, pseudoClass: PseudoClass): string
function determinePseudoClass(input: string, pseudoClass: PseudoClass[]): string
function determinePseudoClass(input: string, pseudoClass: PseudoClass | PseudoClass[]): string {
  // normalize pseudo class input into an array
  const classes = Array.isArray(pseudoClass) ? pseudoClass : [pseudoClass]

  // split the input string into separate classes
  const list = input.split(" ").filter(Boolean)

  // if the list is effectively empty, return an empty string
  if (list.length === 0) {
    return ""
  }

  // process each class class in the input string and add the required prefixes
  const processed = list.map((item) => {
    // if the item includes a color, then there is already a prefix in, so we want to
    // only add the ones that are missing.
    if (item.includes(":")) {
      const parts = item.split(":")
      const unique = Array.from(new Set([...parts.slice(0, parts.length - 1), ...classes]))
        .sort()
        .join(":")
      const actualClass = parts[parts.length - 1]

      return `${unique}:${actualClass}`
    }

    return `${classes.toSorted().join(":")}:${item}`
  })

  return processed.join(" ")
}

export { determinePseudoClass }
